import { useState, useEffect, useRef, useMemo } from "react"
import { Link, useNavigate } from "react-router-dom"
import { FaEdit, FaTrash, FaSms } from "react-icons/fa";
import { toast } from 'react-toastify'
import Button from 'react-bootstrap/Button';
import Modal from 'react-bootstrap/Modal';
import Server from "../../Shared/Server"
import Spinner from '../../Shared/Spinner'
import classnames from "classnames";
import {AgGridReact} from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';

const Zones = () => {
    const [zones, setZones] = useState([])
    const [isLoading, setIsLoading] = useState(false)
    const [isDeleting, setIsDeleting] = useState(false)
    const [show, setShow] = useState(false);
    const [publicID, setPublicID] = useState()
    const gridRef = useRef();

    const navigate = useNavigate()

    useEffect(() => {
        let isMounted = true
        const getZones = async () => {
            setIsLoading(true)
            try {
                const response = await Server.get('/zones/')
                if(isMounted){
                    setZones(response?.data)
                }
            } catch (error) {
                if(error?.response?.status === 401){
                    localStorage.removeItem('user')
                    navigate('/login')
                }else if (error?.response?.status === 403){
                    toast.error(error.response.data.detail);
                    navigate('/');
                }else {
                    toast.error(error?.response?.data?.detail || 'Failed to load zones')
                }
            } finally {
                setIsLoading(false)
            }
        }
        getZones()
        return () => (isMounted = false);
    }, [])

    const columnDefs = useMemo(() => [
        {
            headerName: "#",
            filter: false,
            maxWidth: 70,
            sortable: true,
            valueFormatter: (params) => {
                if (isNaN(params.node.id)) return "";
                return `${Number(params.node.rowIndex) + 1}`;
            },
        },
        {
            headerName: "Actions",
            pinned: 'left',
            minWidth: 180,
            sortable: false,
            filter: false,
            cellRenderer: (e) => {
                if (!e.data) return null;
                if (e.node.rowPinned) return '';
                const hasLocations = e.data.locations > 0;
                return (
                    <div className="d-flex gap-1">
                        <Link
                            className="btn btn-outline-info"
                            to="/send-location-message"
                            state={{ payload: { id: e.data.id, by: 'zone' } }}
                            title="Send SMS"
                        >
                            <FaSms />
                        </Link>
                        <Link
                            className="btn btn-outline-info"
                            to="/update-zone"
                            state={{ payload: e.data }}
                            title="Edit"
                        >
                            <FaEdit />
                        </Link>
                        <button
                            className={classnames('btn btn-outline-danger', { disabled: hasLocations })}
                            onClick={() => !hasLocations && handleShow(e.data.public_id)}
                            title={hasLocations ? "Cannot delete (has locations)" : "Delete"}
                        >
                            <FaTrash />
                        </button>
                    </div>
                );
            },
        },
        {
            headerName: "Name",
            field: "name",
            sortable: true,
            filter: true,
        },
        {
            headerName: "Village",
            field: "village_name",
            sortable: true,
            filter: true,
        },
        {
            headerName: "Collector",
            field: "collector_name",
            sortable: true,
            filter: true,
        },
        {
            headerName: "Locations",
            sortable: false,
            filter: false,
            cellRenderer: (e) => {
                if (!e.data) return null;
                if (e.node.rowPinned) return '';
                return <Link to="/zone-locations" state={{ payload: { public_id: e.data.public_id, name: e.data.name } }}>{e.data.locations}</Link>;
            },
        },
        {
            headerName: "Active",
            sortable: false,
            filter: false,
            cellRenderer: (e) => {
                if (!e.data) return null;
                if (e.node.rowPinned) return '';
                return <Link to="/zone-locations" state={{ payload: { public_id: e.data.public_id, name: e.data.name, status: 'Active' } }}>{e.data.active}</Link>;
            },
        },
        {
            headerName: "Inactive",
            sortable: false,
            filter: false,
            cellRenderer: (e) => {
                if (!e.data) return null;
                if (e.node.rowPinned) return '';
                return <Link to="/zone-locations" state={{ payload: { public_id: e.data.public_id, name: e.data.name, status: 'Inactive' } }}>{e.data.inactive}</Link>;
            },
        },
    ], []);

    const handleClose = () => {
        if (isDeleting) return;
        setShow(false)
    }
    const handleShow = (public_id) => {
        setShow(true)
        setPublicID(public_id)
    };

    const handleDelete = async (public_id) => {
        setIsDeleting(true)
        try {
            const response = await Server.delete(`/zones/${public_id}`)
            toast.success(response.data.detail)
            setZones(prev => prev.filter((zone) => zone.public_id !== public_id))
            setShow(false)
        } catch (error) {
            if(error?.response?.status === 401){
                navigate('/login');
            }else if (error?.response?.status === 403){
                toast.error(error.response.data.detail);
                navigate('/');
            }else{
                toast.error(error?.response?.data?.detail || 'Failed to delete zone');
            }
        } finally {
            setIsDeleting(false)
        }
    }

    const defaultColDef = useMemo(() => ({
        filterParams: { buttons: [] }
    }), []);

    const onFirstDataRendered = (params) => {
        params.api.autoSizeAllColumns();
    }

    const onGridSizeChanged = (params) => {
        params.api.autoSizeAllColumns();
    }

    if(isLoading){
        return <Spinner/>
    }

  return (
    <>
        <div className="row">
            <div className="col-md-12">
                <Link to='/add-zone' className="btn btn-outline-info mb-2" style={{float: 'right'}}>Add Zone</Link>
            </div>
        </div>
        <div className="card">
            <div className="card-header p-2 bg-info text-white d-flex justify-content-center">
                <h3 className="card-title">Zones</h3>
            </div>
            <div className="card-body">
                <div className="row">
                    <div className="col-md-12">
                        <div className="ag-theme-alpine responsive-aggrid" style={{ height: 400, width: '100%' }}>
                            <AgGridReact
                                suppressMenuHide={true}
                                onFirstDataRendered={onFirstDataRendered}
                                onGridSizeChanged={onGridSizeChanged}
                                defaultColDef={defaultColDef}
                                rowData={zones}
                                domLayout={"normal"}
                                rowSelection={"multiple"}
                                animateRows={true}
                                pagination={false}
                                resizable={true}
                                enableCellTextSelection={true}
                                suppressCellFocus={true}
                                ref={gridRef}
                                columnDefs={columnDefs}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <Modal show={show} onHide={handleClose} backdrop={isDeleting ? 'static' : true}>
            <Modal.Header closeButton={!isDeleting}>
                <Modal.Title>Delete Zone</Modal.Title>
            </Modal.Header>
            <Modal.Body>Are you sure you want to delete this zone? This action cannot be undone.</Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={handleClose} disabled={isDeleting}>
                    No
                </Button>
                <Button variant="danger" onClick={() => handleDelete(publicID)} disabled={isDeleting}>
                    {isDeleting ? 'Deleting…' : 'Yes, delete'}
                </Button>
            </Modal.Footer>
        </Modal>
        <br/>
    </>
  )
}

export default Zones
